/*
 * The Comprehensive Wellness Score (CWS), as written in the client's framework
 * document ("Comprehensive Wellness Score Framework: A Point-Based Assessment
 * of Health Parameters"). The point tables, weights, adjustments and
 * categories below are the document's, unchanged.
 *
 *  1. Every result earns points from the range it falls in (section 3).
 *  2. Domain score = points in the domain ÷ maximum possible points × 100 (5.1).
 *  3. Raw score = Σ domain score × domain weight; the weights sum to 100 % (5.2).
 *  4. Final score = raw score + age, BMI, smoking and activity points (5.3).
 *  5. The final score reads as a category, Optimal … Poor (section 6).
 *     Higher is worse: the score counts risk.
 *
 * Where the document is silent or ambiguous the most literal reading is used,
 * marked "Reading:" below; the tests pin each one.
 *
 * Synthetic patient ("Patient A", female, 30). No real data.
 */

/* ---------- Range helpers ----------
 * A parameter is a list of segments in ascending value order. A value takes
 * the first segment it is below (lt) or at-or-below (le); the last segment is
 * open-ended.
 * Reading: where the document leaves a gap between two ranges (TSH 4.0 → 4.1),
 * the gap goes to the range further from optimal; where two ranges overlap
 * (CRP "1-3" and "3-10"), the lower-point range wins, as for hsCRP.
 * `uncovered` marks values the document gives no points for (magnesium above
 * 2.5, say): they score 0, and the page shows them hatched.
 */
const lt = (x) => ({ upper: x, incl: false });
const le = (x) => ({ upper: x, incl: true });
const OPEN = { upper: Infinity, incl: true };
const seg = (label, pts, bound = OPEN, uncovered = false) => ({ label, pts, ...bound, uncovered });
const gap = (label, bound = OPEN) => seg(label, 0, bound, true);

const param = (key, name, unit, segments) => ({ key, name, unit, segments });

const inSegment = (s, value) => (s.incl ? value <= s.upper : value < s.upper);

/* ---------- 3. Parameter categories and point system ---------- */
export const DOMAINS = [
  {
    key: 'cardio',
    name: 'Cardiovascular',
    short: 'Heart',
    weight: 0.25,
    params: [
      param('tc', 'Total cholesterol', 'mg/dL', [
        seg('<160', 0, lt(160)),
        seg('160–199', 1, le(199)),
        seg('200–239', 2, le(239)),
        seg('240–279', 3, le(279)),
        seg('≥280', 4),
      ]),
      param('hdl', 'HDL cholesterol', 'mg/dL', [
        seg('<30', 2, lt(30)),
        seg('30–39', 1, lt(40)),
        seg('40–49', 0, lt(50)),
        seg('50–59', -1, lt(60)),
        seg('≥60', -2),
      ]),
      param('ldl', 'LDL cholesterol', 'mg/dL', [
        seg('<70', 0, lt(70)),
        seg('70–99', 1, le(99)),
        seg('100–129', 2, le(129)),
        seg('130–159', 3, le(159)),
        seg('≥160', 4),
      ]),
      param('tg', 'Triglycerides', 'mg/dL', [
        seg('<100', 0, lt(100)),
        seg('100–149', 1, le(149)),
        seg('150–199', 2, le(199)),
        seg('200–499', 3, le(499)),
        seg('≥500', 4),
      ]),
      param('sbp', 'Systolic BP', 'mmHg', [
        seg('<120', 0, lt(120)),
        seg('120–129', 1, le(129)),
        seg('130–139', 2, le(139)),
        seg('140–159', 3, le(159)),
        seg('≥160', 4),
      ]),
      param('hscrp', 'hsCRP', 'mg/L', [seg('<1', 0, lt(1)), seg('1–3', 1, le(3)), seg('>3', 2)]),
      param('apo', 'ApoB : ApoA', 'ratio', [
        seg('<0.5', 0, lt(0.5)),
        seg('0.5–0.69', 1, le(0.69)),
        seg('0.7–0.89', 2, le(0.89)),
        seg('≥0.9', 3),
      ]),
    ],
  },
  {
    key: 'metabolic',
    name: 'Metabolic',
    short: 'Sugar',
    weight: 0.2,
    params: [
      param('fg', 'Fasting glucose', 'mg/dL', [seg('<100', 0, lt(100)), seg('100–125', 2, le(125)), seg('≥126', 4)]),
      param('a1c', 'HbA1c', '%', [seg('<5.7', 0, lt(5.7)), seg('5.7–6.4', 2, le(6.4)), seg('≥6.5', 4)]),
      param('insulin', 'Fasting insulin', 'µIU/mL', [seg('<10', 0, lt(10)), seg('10–15', 1, le(15)), seg('>15', 2)]),
      param('eag', 'Avg glucose (eAG)', 'mg/dL', [
        seg('<100', 0, lt(100)),
        seg('100–125', 1, le(125)),
        seg('126–154', 2, le(154)),
        seg('≥155', 3),
      ]),
    ],
  },
  {
    key: 'inflammation',
    name: 'Inflammation',
    short: 'Inflam.',
    weight: 0.15,
    params: [
      param('il6', 'IL-6', 'pg/mL', [seg('<1.0', 0, lt(1)), seg('1.0–3.0', 1, le(3)), seg('>3.0', 2)]),
      param('crp', 'CRP', 'mg/L', [seg('<1', 0, lt(1)), seg('1–3', 1, le(3)), seg('3–10', 2, le(10)), seg('>10', 3)]),
      param('esr', 'ESR', 'mm/hr', {
        M: [seg('<15', 0, lt(15)), seg('15–30', 1, le(30)), seg('>30', 2)],
        F: [seg('<20', 0, lt(20)), seg('20–35', 1, le(35)), seg('>35', 2)],
      }),
    ],
  },
  {
    key: 'nutrition',
    name: 'Nutrition',
    short: 'Nutrients',
    weight: 0.1,
    params: [
      param('vitd', 'Vitamin D', 'ng/mL', [seg('<20', 2, lt(20)), seg('20–30', 1, le(30)), seg('>30', 0)]),
      param('b12', 'Vitamin B12', 'pg/mL', [seg('<300', 2, lt(300)), seg('300–400', 1, le(400)), seg('>400', 0)]),
      param('folate', 'Folate', 'ng/mL', [seg('<3.0', 2, lt(3)), seg('3.0–5.4', 1, le(5.4)), seg('>5.4', 0)]),
      param('ferritin', 'Ferritin', 'ng/mL', {
        M: [seg('<15', 2, lt(15)), seg('15–29', 1, lt(30)), seg('30–300', 0, le(300)), seg('>300', 2)],
        F: [seg('<15', 2, lt(15)), seg('15–29', 1, lt(30)), seg('30–200', 0, le(200)), seg('>200', 2)],
      }),
      param('mg', 'Magnesium', 'mg/dL', [
        seg('<1.8', 2, lt(1.8)),
        seg('1.8–1.99', 1, lt(2)),
        seg('2.0–2.5', 0, le(2.5)),
        gap('>2.5'),
      ]),
      param('zinc', 'Zinc', 'µg/dL', [
        seg('<60', 2, lt(60)),
        seg('60–69', 1, lt(70)),
        seg('70–120', 0, le(120)),
        gap('>120'),
      ]),
    ],
  },
  {
    key: 'liver',
    name: 'Liver',
    short: 'Liver',
    weight: 0.1,
    params: [
      param('alt', 'ALT', 'U/L', {
        M: [seg('<30', 0, lt(30)), seg('30–60', 1, le(60)), seg('>60', 2)],
        F: [seg('<19', 0, lt(19)), seg('19–40', 1, le(40)), seg('>40', 2)],
      }),
      param('ast', 'AST', 'U/L', {
        M: [seg('<35', 0, lt(35)), seg('35–70', 1, le(70)), seg('>70', 2)],
        F: [seg('<25', 0, lt(25)), seg('25–50', 1, le(50)), seg('>50', 2)],
      }),
      param('ggt', 'GGT', 'U/L', {
        M: [seg('<30', 0, lt(30)), seg('30–60', 1, le(60)), seg('>60', 2)],
        F: [seg('<20', 0, lt(20)), seg('20–40', 1, le(40)), seg('>40', 2)],
      }),
      param('bili', 'Bilirubin total', 'mg/dL', [
        seg('<0.3', 2, lt(0.3)),
        seg('0.3–1.2', 0, le(1.2)),
        seg('1.3–2.0', 1, le(2)),
        seg('>2.0', 2),
      ]),
      param('albumin', 'Albumin', 'g/dL', [
        seg('<3.0', 2, lt(3)),
        seg('3.0–3.5', 1, lt(3.6)),
        seg('3.6–5.0', 0, le(5)),
        gap('>5.0'),
      ]),
    ],
  },
  {
    key: 'kidney',
    name: 'Kidney',
    short: 'Kidney',
    weight: 0.05,
    params: [
      param('egfr', 'eGFR', 'mL/min', [
        seg('<30', 4, lt(30)),
        seg('30–44', 3, lt(45)),
        seg('45–59', 2, lt(60)),
        seg('60–89', 1, lt(90)),
        seg('≥90', 0),
      ]),
      param('creat', 'Creatinine', 'mg/dL', {
        M: [gap('<0.6', lt(0.6)), seg('0.6–1.2', 0, le(1.2)), seg('1.3–1.5', 1, le(1.5)), seg('>1.5', 2)],
        F: [gap('<0.5', lt(0.5)), seg('0.5–1.1', 0, le(1.1)), seg('1.2–1.4', 1, le(1.4)), seg('>1.4', 2)],
      }),
      param('bun', 'BUN', 'mg/dL', [gap('<7', lt(7)), seg('7–20', 0, le(20)), seg('21–30', 1, le(30)), seg('>30', 2)]),
      param('ua', 'Uric acid', 'mg/dL', {
        M: [gap('<3.5', lt(3.5)), seg('3.5–7.0', 0, le(7)), seg('7.1–8.0', 1, le(8)), seg('>8.0', 2)],
        F: [gap('<2.5', lt(2.5)), seg('2.5–6.0', 0, le(6)), seg('6.1–7.0', 1, le(7)), seg('>7.0', 2)],
      }),
    ],
  },
  {
    key: 'thyroid',
    name: 'Thyroid',
    short: 'Thyroid',
    weight: 0.05,
    params: [
      param('tsh', 'TSH', 'µIU/mL', [
        seg('<0.1', 2, lt(0.1)),
        seg('0.1–0.39', 1, lt(0.4)),
        seg('0.4–4.0', 0, le(4)),
        seg('4.1–10', 1, le(10)),
        seg('>10', 2),
      ]),
      param('ft4', 'Free T4', 'ng/dL', [
        seg('<0.6', 2, lt(0.6)),
        seg('0.6–0.79', 1, lt(0.8)),
        seg('0.8–1.8', 0, le(1.8)),
        seg('1.81–2.2', 1, le(2.2)),
        seg('>2.2', 2),
      ]),
      param('ft3', 'Free T3', 'pg/mL', [
        seg('<1.8', 2, lt(1.8)),
        seg('1.8–2.2', 1, lt(2.3)),
        seg('2.3–4.2', 0, le(4.2)),
        seg('4.3–5.0', 1, le(5)),
        seg('>5.0', 2),
      ]),
      param('tpo', 'Anti-TPO', 'IU/mL', [seg('<35', 0, lt(35)), seg('35–100', 1, le(100)), seg('>100', 2)]),
    ],
  },
  {
    key: 'blood',
    name: 'Blood count',
    short: 'Blood',
    weight: 0.05,
    params: [
      param('hb', 'Haemoglobin', 'g/dL', {
        M: [seg('<12.0', 2, lt(12)), seg('12.0–13.4', 1, lt(13.5)), seg('13.5–17.5', 0, le(17.5)), gap('>17.5')],
        F: [seg('<11.0', 2, lt(11)), seg('11.0–11.9', 1, lt(12)), seg('12.0–15.5', 0, le(15.5)), gap('>15.5')],
      }),
      param('mcv', 'MCV', 'fL', [
        seg('<70', 2, lt(70)),
        seg('70–79', 1, lt(80)),
        seg('80–100', 0, le(100)),
        seg('101–110', 1, le(110)),
        seg('>110', 2),
      ]),
      param('wbc', 'WBC', '×10⁹/L', [
        seg('<3.0', 2, lt(3)),
        seg('3.0–3.9', 1, lt(4)),
        seg('4.0–11.0', 0, le(11)),
        seg('11.1–15.0', 1, le(15)),
        seg('>15.0', 2),
      ]),
      param('plt', 'Platelets', '×10⁹/L', [
        seg('<100', 2, lt(100)),
        seg('100–149', 1, lt(150)),
        seg('150–450', 0, le(450)),
        seg('451–600', 1, le(600)),
        seg('>600', 2),
      ]),
    ],
  },
  {
    key: 'hormones',
    name: 'Hormones',
    short: 'Hormones',
    weight: 0.05,
    params: [
      /* Reading: only a morning sample is graded beyond the 0-point range, so the page uses one. */
      param('cortisol', 'Cortisol (am)', 'µg/dL', [
        seg('<3', 2, lt(3)),
        seg('3–4.9', 1, lt(5)),
        seg('5–25', 0, le(25)),
        seg('25.1–30', 1, le(30)),
        seg('>30', 2),
      ]),
      param('testo', 'Testosterone', 'ng/dL', {
        M: [seg('<200', 2, lt(200)), seg('200–299', 1, lt(300)), seg('300–1000', 0, le(1000)), seg('>1000', 2)],
        F: [seg('<15', 2, lt(15)), seg('15–70', 0, le(70)), seg('71–100', 1, le(100)), seg('>100', 2)],
      }),
      /* Reading: the 2-point row is "N/A", so estradiol tops out at 1 point. Women: premenopausal ranges. */
      param('e2', 'Estradiol', 'pg/mL', {
        M: [seg('<20', 1, lt(20)), seg('20–60', 0, le(60)), seg('>60', 1)],
        F: [seg('<30', 1, lt(30)), seg('30–400', 0, le(400)), gap('>400')],
      }),
    ],
  },
];

/* ---------- 4. Demographic and lifestyle adjustments ---------- */
export const ADJUSTMENTS = [
  {
    key: 'age',
    name: 'Age',
    unit: 'years',
    segments: [
      seg('<30', 0, lt(30)),
      seg('30–39', 1, lt(40)),
      seg('40–49', 2, lt(50)),
      seg('50–59', 3, lt(60)),
      seg('≥60', 4),
    ],
  },
  {
    key: 'bmi',
    name: 'BMI',
    unit: 'kg/m²',
    segments: [
      seg('<18.5', 4, lt(18.5)),
      seg('18.5–24.9', 0, le(24.9)),
      seg('25–29.9', 1, le(29.9)),
      seg('30–34.9', 2, le(34.9)),
      seg('35–39.9', 3, le(39.9)),
      seg('≥40', 4),
    ],
  },
  {
    key: 'smoking',
    name: 'Smoking',
    categories: [
      { key: 'never', label: 'Never', pts: 0 },
      { key: 'quit-over-5y', label: 'Quit >5 y', pts: 1 },
      { key: 'quit-under-5y', label: 'Quit <5 y', pts: 2 },
      { key: 'under-10', label: '<10 a day', pts: 3 },
      { key: '10-plus', label: '≥10 a day', pts: 4 },
    ],
  },
  {
    key: 'activity',
    name: 'Activity',
    categories: [
      { key: '150-plus', label: '≥150 min/wk', pts: -2 },
      { key: '75-149', label: '75–149 min', pts: -1 },
      { key: 'under-75', label: '<75 min', pts: 0 },
      { key: 'sedentary', label: 'Sedentary', pts: 2 },
    ],
  },
];

/* ---------- 6. Risk score interpretation (higher is worse) ----------
 * Reading: the bands are whole numbers (<10, 10-20, 21-30, 31-50, >50), so the
 * final score is rounded to a whole number before it is banded.
 */
export const TIERS = [
  {
    tier: 'Optimal',
    range: '<10',
    lo: -Infinity,
    hi: 9,
    meaning: 'Excellent health status',
    action: 'Maintain current healthy behaviours',
  },
  {
    tier: 'Good',
    range: '10–20',
    lo: 10,
    hi: 20,
    meaning: 'Minor issues that require attention',
    action: 'Address specific areas of concern',
  },
  {
    tier: 'Fair',
    range: '21–30',
    lo: 21,
    hi: 30,
    meaning: 'Multiple results outside optimal range',
    action: 'Focused lifestyle interventions',
  },
  {
    tier: 'Suboptimal',
    range: '31–50',
    lo: 31,
    hi: 50,
    meaning: 'Significant deviations requiring intervention',
    action: 'Comprehensive lifestyle modification',
  },
  {
    tier: 'Poor',
    range: '>50',
    lo: 51,
    hi: Infinity,
    meaning: 'High-risk status requiring immediate attention',
    action: 'Medical evaluation and intervention',
  },
];

export function tierFor(score) {
  const s = Math.round(score);
  return TIERS.find((t) => s <= t.hi);
}

/* ---------- Scoring ---------- */
export function segmentsFor(p, sex = 'F') {
  return Array.isArray(p.segments) ? p.segments : p.segments[sex];
}

/* The segment a value falls in: { index, pts, label, uncovered }. */
export function pointsFor(p, value, sex = 'F') {
  const segments = segmentsFor(p, sex);
  const index = segments.findIndex((s) => inSegment(s, value));
  const { pts, label, uncovered } = segments[index];
  return { index, pts, label, uncovered };
}

/* Most points a parameter can earn from a documented range. */
export function maxPointsOf(p, sex = 'F') {
  return Math.max(
    ...segmentsFor(p, sex)
      .filter((s) => !s.uncovered)
      .map((s) => s.pts),
  );
}

/*
 * 5.1 Domain score = points ÷ maximum possible points × 100.
 * Reading: the maximum counts only the parameters that were measured, so an
 * untested parameter neither adds points nor raises the maximum. A domain
 * with nothing measured scores 0.
 */
export function scoreDomain(domain, values, sex = 'F') {
  const params = domain.params
    .filter((p) => values[p.key] !== undefined && values[p.key] !== null)
    .map((p) => ({
      key: p.key,
      name: p.name,
      unit: p.unit,
      value: values[p.key],
      segments: segmentsFor(p, sex),
      max: maxPointsOf(p, sex),
      ...pointsFor(p, values[p.key], sex),
    }));
  const points = params.reduce((sum, p) => sum + p.pts, 0);
  const max = params.reduce((sum, p) => sum + p.max, 0);
  return {
    key: domain.key,
    name: domain.name,
    short: domain.short,
    weight: domain.weight,
    params,
    points,
    max,
    score: max ? (points / max) * 100 : 0,
  };
}

export function adjustmentPoints(lifestyle) {
  return ADJUSTMENTS.map((a) => {
    const value = lifestyle[a.key];
    const index = a.segments
      ? a.segments.findIndex((s) => inSegment(s, value))
      : a.categories.findIndex((c) => c.key === value);
    const { pts, label } = (a.segments || a.categories)[index];
    return { key: a.key, name: a.name, value, index, pts, label };
  });
}

/*
 * wellnessScore({ sex, values, lifestyle }, { weighted })
 *   5.2 raw = Σ domain score × weight; 5.3 final = raw + adjustments.
 * `weighted: false` is only the page's "plain average" comparison (each of the
 * nine domains counts 1/9); the framework itself is weighted.
 */
export function wellnessScore({ sex = 'F', values, lifestyle }, { weighted = true } = {}) {
  const domains = DOMAINS.map((d) => {
    const s = scoreDomain(d, values, sex);
    const share = weighted ? d.weight : 1 / DOMAINS.length;
    return { ...s, share, contribution: s.score * share };
  });
  const raw = domains.reduce((sum, d) => sum + d.contribution, 0);
  const adjustments = adjustmentPoints(lifestyle);
  const adjustTotal = adjustments.reduce((sum, a) => sum + a.pts, 0);
  const final = raw + adjustTotal;
  const score = Math.round(final);
  return { domains, raw, adjustments, adjustTotal, final, score, band: tierFor(score), tier: tierFor(score).tier };
}

/* ---------- The page's synthetic patient and its "what if" switches ---------- */

/* Patient A: female, 30, BMI 23.4, never smoked, under 75 active minutes a week. */
export const SAMPLE = {
  sex: 'F',
  lifestyle: { age: 30, bmi: 23.4, smoking: 'never', activity: 'under-75' },
  values: {
    // Cardiovascular
    tc: 172,
    hdl: 54,
    ldl: 92,
    tg: 110,
    sbp: 118,
    hscrp: 0.8,
    apo: 0.58,
    // Metabolic (eAG = 28.7 × HbA1c − 46.7)
    fg: 88,
    a1c: 5.0,
    insulin: 7,
    eag: 97,
    // Inflammation
    il6: 0.9,
    crp: 0.8,
    esr: 14,
    // Nutrition
    vitd: 34,
    b12: 340,
    folate: 7.2,
    ferritin: 48,
    mg: 2.1,
    zinc: 78,
    // Liver
    alt: 17,
    ast: 21,
    ggt: 16,
    bili: 0.6,
    albumin: 4.3,
    // Kidney
    egfr: 108,
    creat: 0.7,
    bun: 11,
    ua: 4.2,
    // Thyroid
    tsh: 2.6,
    ft4: 1.2,
    ft3: 3.1,
    tpo: 22,
    // Blood count
    hb: 12.8,
    mcv: 88,
    wbc: 6.8,
    plt: 260,
    // Hormones
    cortisol: 14,
    testo: 32,
    e2: 120,
  },
};

/* What each finding switch changes on the report. */
export const FINDINGS = [
  { key: 'iron', name: 'Iron-deficiency anaemia', values: { ferritin: 9, hb: 10.6, mcv: 74 } },
  { key: 'vitD', name: 'Vitamin D deficiency', values: { vitd: 14 } },
  { key: 'esr', name: 'Raised ESR', values: { esr: 28 } },
  { key: 'prediabetes', name: 'Prediabetes', values: { fg: 112, a1c: 6.1, insulin: 13, eag: 128 } },
];

/* What each lifestyle switch changes. */
export const LIFESTYLE_SWITCHES = [
  { key: 'active', lifestyle: { activity: '150-plus' } },
  { key: 'smoker', lifestyle: { smoking: 'under-10' } },
];

export function profileFor(switches = {}) {
  const values = { ...SAMPLE.values };
  FINDINGS.filter((f) => switches[f.key]).forEach((f) => Object.assign(values, f.values));
  const lifestyle = { ...SAMPLE.lifestyle };
  LIFESTYLE_SWITCHES.filter((s) => switches[s.key]).forEach((s) => Object.assign(lifestyle, s.lifestyle));
  return { sex: SAMPLE.sex, values, lifestyle };
}

/*
 * computeScore({ iron, vitD, esr, prediabetes, active, smoker, plainAverage })
 *   -> wellnessScore's result plus `method`, `weightedScore` (the framework's
 *      own score, whichever method is shown) and `findings` (switched-on names).
 */
export function computeScore(switches = {}) {
  const profile = profileFor(switches);
  const weighted = wellnessScore(profile);
  const r = switches.plainAverage ? wellnessScore(profile, { weighted: false }) : weighted;
  return {
    ...r,
    profile,
    method: switches.plainAverage ? 'plain-average' : 'weighted',
    weightedScore: weighted.score,
    findings: FINDINGS.filter((f) => switches[f.key]).map((f) => f.name),
  };
}
